// ─────────────────────────────────────────────
//  assignmentRow.cells.js — dropdown cell renderers for AssignmentRow.
//  Every cell is a dropdown fed by the day's master lists. A saved value
//  that's missing from its list is kept as an extra "orphan" option so the
//  row never silently loses data.
//  Extracted so AssignmentRow.js stays under 500 lines.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import { shiftCodeOptions, callOrderOptions } from "../domain/schedule";

const { color, font, space, radius } = tokens;

// ── Dropdown option lists ─────────────────────
const SHIFT_CODE_OPTIONS = shiftCodeOptions();
const CALL_OPTIONS = callOrderOptions(30);

// Time dropdowns in 15-minute steps (HH:MM, matches saved data).
const timeSlots = (fromHour, toHour) => {
  const opts = [];
  for (let h = fromHour; h <= toHour; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === toHour && m > 0) break;
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
};
// Starts: 06:00–19:00. Ends run later — shifts can finish into the evening.
export const START_TIME_OPTIONS = timeSlots(6, 19);
export const END_TIME_OPTIONS = timeSlots(6, 23);
// CRNA departure (when they leave for the day): late morning → late evening.
export const DEPART_TIME_OPTIONS = timeSlots(10, 23);

// ── Edit-mode cell builders ───────────────────

export function buildEditCells({ a, saving, set, crnas, doctors, ahp, attending,
  handleShiftCodeChange, handleCrnaChange, handleCallOrderChange, isTemplateLocked, templateOr, surgeons = [],
  assignedAhpIds, assignedAttendingIds }) {

  // Double-book guard for CRNAs/AAs ONLY (a provider staffs exactly one room):
  // a CRNA booked on another saved row is hidden from this row's primary CRNA
  // picker, but the row always keeps its OWN current pick so an existing
  // assignment never vanishes from its own cell. Doctors are intentionally NOT
  // filtered — a supervising anesthesiologist covers multiple rooms by design
  // (see the supervisor overload flag), so they must stay pickable everywhere.
  const availableCrnas = crnas.filter(
    (c) => !assignedAhpIds?.has(c.id) || c.id === a.ahpId
  );

  const orEditCell = isTemplateLocked ? (
    <span style={st.bodyText}>{templateOr || a.orRoom}</span>
  ) : (
    <input aria-label="OR#" type="text" inputMode="numeric" value={a.orRoom || ""}
      onChange={set("orRoom")} style={{ ...st.input, width: 38 }} disabled={saving} />
  );

  // Master-list dropdowns keep a saved value visible even if it left the list.
  const surgeonList = [...surgeons].sort((x, y) => x.localeCompare(y));
  const surgeonOrphan = a.surgeon && !surgeonList.includes(a.surgeon);
  const startOrphan = a.startTime && !START_TIME_OPTIONS.includes(a.startTime);
  const endOrphan = a.endTime && !END_TIME_OPTIONS.includes(a.endTime);
  // Staff selects: when the saved id is no longer in the list (departed,
  // cross-facility roster change), surface the saved display name instead.
  const crnaOrphan = a.ahpId && !crnas.some((c) => c.id === a.ahpId);
  const mdOrphan = a.attendingId && !doctors.some((d) => d.id === a.attendingId);

  return {
    orRoom: orEditCell,
    startTime: (
      <select aria-label="Start time" value={a.startTime || ""}
        onChange={set("startTime")} style={{ ...st.input, minWidth: 58 }} disabled={saving}>
        <option value="">—</option>
        {startOrphan && <option value={a.startTime}>{a.startTime}</option>}
        {START_TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    ),
    endTime: (
      <select aria-label="End time" value={a.endTime || ""}
        onChange={set("endTime")} style={{ ...st.input, minWidth: 58 }} disabled={saving}>
        <option value="">—</option>
        {endOrphan && <option value={a.endTime}>{a.endTime}</option>}
        {END_TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    ),
    surgeon: (
      <select aria-label="Surgeon" value={a.surgeon || ""} onChange={set("surgeon")}
        style={{ ...st.input, minWidth: 70 }} disabled={saving}>
        <option value="">Pick a surgeon…</option>
        {surgeonOrphan && <option value={a.surgeon}>{a.surgeon}</option>}
        {surgeonList.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    ),
    crna: (
      <span style={st.cellStack}>
        <select aria-label="CRNA" value={a.ahpId || ""} onChange={handleCrnaChange}
          style={{ ...st.input, minWidth: 88 }} disabled={saving}>
          <option value="">Pick a CRNA/AA…</option>
          {crnaOrphan && <option value={a.ahpId}>{a.ahpName || "(unavailable)"}</option>}
          {availableCrnas.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name + (c.crossFacility && c.facilityName ? ` · ${c.facilityName}` : "")}
            </option>
          ))}
        </select>
        {(a.ahpId || a.ahpName) && (() => {
          // TIME = the CRNA's shift number. Prefer the roster member's shiftCode
          // (editable per person); fall back to the imported per-case value the
          // sheet printed after the name (stored on the assignment). This makes
          // the sheet number visible even when the CRNA isn't on today's roster.
          // No number anywhere → blank ("TIME…") — never assign a time we don't have.
          const shiftVal = String(ahp?.shiftCode ?? a.ahpShiftCode ?? "");
          const shiftOrphan = shiftVal && !SHIFT_CODE_OPTIONS.some((o) => String(o.value) === shiftVal);
          return (
            <select aria-label={`Shift time for ${ahp?.name || a.ahpName || "CRNA"}`}
              value={shiftVal} onChange={handleShiftCodeChange}
              style={{ ...st.input, ...st.subSelect }} disabled={saving}>
              <option value="">TIME…</option>
              {shiftOrphan && <option value={shiftVal}>{shiftVal}</option>}
              {SHIFT_CODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          );
        })()}
      </span>
    ),
    attending: (
      <span style={st.cellStack}>
        <select aria-label="Attending" value={a.attendingId || ""} onChange={set("attendingId")}
          style={{ ...st.input, minWidth: 88 }} disabled={saving}>
          <option value="">Pick a doctor…</option>
          {mdOrphan && <option value={a.attendingId}>{a.attendingName || "(unavailable)"}</option>}
          {doctors.map((d) => (
            <option key={d.id} value={d.id}>
              {(d.shortCode ? `${d.shortCode} — ` : "") + d.name +
                (d.crossFacility && d.facilityName ? ` · ${d.facilityName}` : "")}
            </option>
          ))}
        </select>
        {a.attendingId && (
          <select aria-label={`Call order for ${attending?.name || "doctor"}`}
            value={attending?.callOrder ?? ""} onChange={handleCallOrderChange}
            style={{ ...st.input, ...st.subSelect }} disabled={saving}>
            <option value="">Call order…</option>
            {CALL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </span>
    ),
  };
}

// ── Shared styles ─────────────────────────────
export const st = {
  bodyText: {
    fontSize: font.size.body,
    color: color.text.primary,
    display: "inline-flex",
    alignItems: "center",
    gap: space.xs,
  },
  input: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    // Compact: tight padding + label-size text so all columns fit without
    // horizontal scroll on the Daily List board table.
    padding: "4px 6px",
    fontSize: font.size.label,
    fontFamily: font.family,
    boxSizing: "border-box",
    width: "100%",
  },
  subSelect: {
    padding: "4px 6px",
    fontSize: font.size.label,
    marginTop: 2,
  },
  cellStack: {
    display: "inline-flex",
    flexDirection: "column",
    gap: 2,
  },
};
